import React, { useEffect, useState, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Overview from './pages/Overview';
import Banks from './pages/Banks';
import Sales from './pages/Sales';
import Stock from './pages/Stock';
import CRM from './pages/CRM';
import Debts from './pages/Debts';
import PlanFact from './pages/PlanFact';
import PlanFactIO from './pages/PlanFactIO';
import Sheets from './pages/Sheets';
import FinDirector from './pages/Academy';
import Accounting from './pages/Accounting';
import Retail from './pages/Retail';
import FDM from './pages/FDM';
import Bober from './pages/Bober';
import KPK from './pages/KPK';
import Cash from './pages/Cash';
import PartnerPortal from './pages/PartnerPortal';
import PartnerSaas from './pages/PartnerSaas';
import PartnerOs from './pages/PartnerOs';
import Procurement from './pages/Procurement';
import BitrixAgent from './pages/BitrixAgent';
import AiRop from './pages/AiRop';
import Seo from './pages/Seo';
import MarketingFunnel from './pages/MarketingFunnel';
import Login from './pages/Login';
import { triggerSync } from './api';

// ─── Auth context ─────────────────────────────────────────────────────────────

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

const debugLog = (runId, hypothesisId, location, message, data = {}) => {
  // #region agent log
  fetch('http://127.0.0.1:7714/ingest/438ca71a-251c-44c8-9452-87087ef7113a',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'1ce0d7'},body:JSON.stringify({sessionId:'1ce0d7',runId,hypothesisId,location,message,data,timestamp:Date.now()})}).catch(()=>{});
  // #endregion
};

function getStoredUser() {
  try {
    const raw = localStorage.getItem('auth_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Role-based access: explicit allowlist per route
const ROUTE_ROLES = {
  viewer:    ['admin', 'manager', 'viewer'],
  manager:   ['admin', 'manager'],
  franchise: ['admin', 'manager', 'franchise', 'retail'],
  franchiseMenu: ['admin', 'manager', 'franchise', 'franchise_menu'],
  admin:     ['admin'],
  buyer:     ['admin', 'manager', 'buyer'],
};

function hasRole(user, minRole) {
  return (ROUTE_ROLES[minRole] || ROUTE_ROLES.viewer).includes(user?.role);
}

// ─── Route guards ─────────────────────────────────────────────────────────────

function PrivateRoute({ children, minRole = 'viewer' }) {
  const token = localStorage.getItem('auth_token');
  const user = getStoredUser();

  if (!token || !user) return <Navigate to="/login" replace />;

  // buyer видит только /findirector и /procurement
  if (user.role === 'buyer' && minRole !== 'buyer') {
    return <Navigate to="/findirector" replace />;
  }

  // retail видит /retail и Bitrix AI agent
  if (
    user.role === 'retail' &&
    minRole !== 'franchise' &&
    window.location.pathname !== '/crm/ai-agent'
  ) {
    return <Navigate to="/retail" replace />;
  }

  // franchise пользователи не видят главный экран — редирект на /retail
  if (user.role === 'franchise' && minRole === 'viewer' && window.location.pathname === '/') {
    return <Navigate to="/retail" replace />;
  }

  // franchise_menu пользователи видят только блок франшизы
  if (user.role === 'franchise_menu' && minRole === 'viewer' && window.location.pathname === '/') {
    return <Navigate to="/fdm" replace />;
  }

  if (!hasRole(user, minRole)) {
    if (user.role === 'buyer') return <Navigate to="/findirector" replace />;
    if (user.role === 'franchise_menu') return <Navigate to="/fdm" replace />;
    return <Navigate to="/retail" replace />;
  }

  return children;
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const [syncing, setSyncing] = useState(false);
  const [user, setUser] = useState(getStoredUser);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    debugLog('initial', 'H1', 'App.js:104', 'app boot reached', {
      pathname: window.location.pathname,
      search: window.location.search,
      hasToken: !!localStorage.getItem('auth_token'),
      role: getStoredUser()?.role || '',
    });
    const onError = (event) => {
      debugLog('initial', 'H2', 'App.js:111', 'window error', {
        message: event.message || '',
        source: event.filename || '',
        line: event.lineno || 0,
      });
    };
    const onRejection = (event) => {
      debugLog('initial', 'H2', 'App.js:119', 'unhandled rejection', {
        reason: String(event.reason || ''),
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try { await triggerSync(); } finally { setSyncing(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setUser(null);
    window.location.href = '/login';
  };

  const token = localStorage.getItem('auth_token');

  // Not logged in → only show login page
  if (!token || !user) {
    const afterLogin = () => {
      const u = getStoredUser();
      const role = u?.role;
      const dest = role === 'buyer'
        ? '/findirector'
        : role === 'retail'
          ? '/retail'
          : role === 'franchise_menu'
            ? '/fdm'
            : '/';
      window.location.replace(dest);
    };
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login onLogin={afterLogin} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <AuthContext.Provider value={{ user, hasRole: (r) => hasRole(user, r), logout: handleLogout }}>
      <BrowserRouter>
        <div style={{ display: 'flex', minHeight: '100vh' }}>
          <Sidebar
            onSync={handleSync}
            syncing={syncing}
            user={user}
            onLogout={handleLogout}
            mobileOpen={sidebarOpen}
            onMobileClose={() => setSidebarOpen(false)}
          />
          {/* Mobile top bar */}
          <div className="mobile-topbar">
            <button
              className="hamburger"
              onClick={() => setSidebarOpen(o => !o)}
              aria-label="Меню"
            >
              <span /><span /><span />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src="/logo.png?v=6" alt="Ключ под ключ" style={{ width: 24, height: 24, objectFit: 'contain' }} />
              <span style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>Ключ под ключ</span>
            </div>
          </div>
          <main className="main-content" style={{ marginLeft: 230, flex: 1, minWidth: 0, minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: '24px 32px 28px', maxWidth: 'calc(100vw - 230px)' }}>
            <Routes>
              {/* viewer+ */}
              <Route path="/" element={<PrivateRoute><Overview /></PrivateRoute>} />
              <Route path="/sales" element={<PrivateRoute><Sales /></PrivateRoute>} />
              <Route path="/retail" element={<PrivateRoute minRole="franchise"><Retail /></PrivateRoute>} />
              <Route path="/marketing" element={<PrivateRoute minRole="franchise"><MarketingFunnel /></PrivateRoute>} />
              <Route path="/crm" element={<PrivateRoute><CRM /></PrivateRoute>} />
              <Route path="/crm/ai-agent" element={<PrivateRoute><BitrixAgent /></PrivateRoute>} />
              <Route path="/ai-rop" element={<PrivateRoute minRole="manager"><AiRop /></PrivateRoute>} />
              <Route path="/seo" element={<PrivateRoute minRole="manager"><Seo /></PrivateRoute>} />

              {/* manager+ */}
              <Route path="/banks" element={<PrivateRoute minRole="manager"><Banks /></PrivateRoute>} />
              <Route path="/cash" element={<PrivateRoute minRole="manager"><Cash /></PrivateRoute>} />
              <Route path="/fdm" element={<PrivateRoute minRole="franchiseMenu"><FDM /></PrivateRoute>} />
              <Route path="/partner" element={<PrivateRoute minRole="franchiseMenu"><PartnerPortal /></PrivateRoute>} />
              <Route path="/partner-saas" element={<PrivateRoute minRole="franchiseMenu"><PartnerSaas /></PrivateRoute>} />
              <Route path="/partner-os" element={<PrivateRoute minRole="franchiseMenu"><PartnerOs /></PrivateRoute>} />
              <Route path="/bober" element={<PrivateRoute minRole="franchiseMenu"><Bober /></PrivateRoute>} />
              <Route path="/kpk" element={<PrivateRoute minRole="franchiseMenu"><KPK /></PrivateRoute>} />
              <Route path="/stock" element={<PrivateRoute minRole="manager"><Stock /></PrivateRoute>} />
              <Route path="/procurement" element={<PrivateRoute minRole="buyer"><Procurement /></PrivateRoute>} />
              <Route path="/debts" element={<PrivateRoute minRole="manager"><Debts /></PrivateRoute>} />
              <Route path="/planfact" element={<PrivateRoute minRole="manager"><PlanFact /></PrivateRoute>} />
              <Route path="/planfact-io" element={<PrivateRoute minRole="manager"><PlanFactIO /></PrivateRoute>} />
              <Route path="/sheets" element={<PrivateRoute minRole="manager"><Sheets /></PrivateRoute>} />
              <Route path="/findirector" element={<PrivateRoute minRole="buyer"><FinDirector /></PrivateRoute>} />
              <Route path="/academy" element={<Navigate to="/findirector" replace />} />
              <Route path="/accounting" element={<PrivateRoute minRole="manager"><Accounting /></PrivateRoute>} />

              <Route path="/payroll" element={<Navigate to="/planfact?tab=payroll" replace />} />
              <Route path="/payroll-editor" element={<Navigate to="/planfact?tab=payroll" replace />} />

              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <footer className="kp-foot">
              <div>
                <div className="slogan">Ключ под ключ</div>
                <div className="desc">Ключная мастерская · аналитика бизнеса</div>
              </div>
              <div className="contacts">
                <b>kluchpodkluch.ru</b><br />
                +7 (812) 982-06-10
              </div>
            </footer>
          </main>
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}
